import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { ElectronPrintingApi } from 'services/printing/renderer/ElectronPrintingApi';
import type {
  IPPConfig,
  PrinterCapabilities,
  PrinterConnectionReport,
  PrinterDiagnosticEntry,
  PrintJob,
  PrintProfileId,
} from 'services/printing/types';
import {
  FiPrinter,
  FiCheckCircle,
  FiXCircle,
  FiSettings,
  FiInfo,
} from 'react-icons/fi';

type LogSource = 'connect' | 'print';

interface LogEntry extends PrinterDiagnosticEntry {
  id: string;
  timestamp: string;
  source: LogSource;
}

interface ProfileOption {
  id: PrintProfileId;
  label: string;
  description: string;
  format: 'A4' | 'A3';
  sourceFormat?: 'A4' | 'A3';
  jobType: PrintJob['type'];
  requiresStapling?: boolean;
  requiresFolding?: boolean;
  supportsCustomFinishings?: boolean;
}

const PROFILE_OPTIONS: ProfileOption[] = [
  {
    id: 'A4_SIMPLE',
    label: 'Profil A4',
    description: '2 pages blanches en A4 pour valider le chemin simple.',
    format: 'A4',
    jobType: 'label',
  },
  {
    id: 'A4_BOOKLET',
    label: 'A5-on-A4 booklet profile',
    description: '4 A5 pages imposed on A4 with booklet folding and stapling.',
    format: 'A4',
    jobType: 'assembly',
    requiresFolding: true,
    requiresStapling: true,
    supportsCustomFinishings: true,
  },
  {
    id: 'A3_BOOKLET',
    label: 'A4-on-A3 booklet profile',
    description: '4 A4 pages imposed on A3 with booklet folding and stapling.',
    format: 'A3',
    sourceFormat: 'A4',
    jobType: 'assembly',
    requiresFolding: true,
    requiresStapling: true,
    supportsCustomFinishings: true,
  },
];

const RicohTestPage: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [printerStatus, setPrinterStatus] = useState<
    'unknown' | 'online' | 'offline' | 'error'
  >('unknown');
  const [capabilities, setCapabilities] = useState<PrinterCapabilities | null>(
    null,
  );
  const [connectionReport, setConnectionReport] =
    useState<PrinterConnectionReport | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [activeProfile, setActiveProfile] = useState<PrintProfileId | null>(
    null,
  );
  const [electronAvailable, setElectronAvailable] = useState(true);
  const [finishingsOverrides, setFinishingsOverrides] = useState<
    Record<PrintProfileId, string>
  >({
    A4_SIMPLE: '',
    A4_BOOKLET: '',
    A3_BOOKLET: '',
  });
  const [pageCounts, setPageCounts] = useState<Record<PrintProfileId, number>>({
    A4_SIMPLE: 2,
    A4_BOOKLET: 4,
    A3_BOOKLET: 4,
  });

  const [defaultConfig] = useState<IPPConfig>(
    ElectronPrintingApi.createDefaultConfig(),
  );

  const appendLogs = (
    entries: PrinterDiagnosticEntry[],
    source: LogSource,
    reset = false,
  ) => {
    setLogs(prev => {
      const base = reset ? [] : prev;
      const timestamp = new Date().toISOString();
      const items = entries.map((entry, index) => ({
        ...entry,
        id: `${timestamp}-${source}-${index}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        timestamp,
        source,
      }));
      return [...base, ...items];
    });
  };

  const pushLog = (
    level: PrinterDiagnosticEntry['level'],
    message: string,
    source: LogSource,
    details?: Record<string, unknown>,
  ) => {
    appendLogs([{ level, message, details }], source, false);
  };

  const derivePrinterStatus = (
    report: PrinterConnectionReport | null,
  ): 'unknown' | 'online' | 'offline' | 'error' => {
    if (!report) return 'unknown';
    if (!report.connected) return 'error';
    if (report.state?.isIdle) return 'online';
    const keyword = report.state?.keyword ?? '';
    if (keyword.includes('process')) return 'online';
    if (keyword.includes('idle')) return 'online';
    if (keyword.includes('stop')) return 'offline';
    if (keyword.includes('error')) return 'error';
    return 'online';
  };

  useEffect(() => {
    checkElectronAvailability();
  }, []);

  const checkElectronAvailability = async () => {
    try {
      const available = await ElectronPrintingApi.isAvailable();
      setElectronAvailable(available);
      if (!available) {
        appendLogs(
          [
            {
              level: 'warning',
              message: 'Electron API non disponible - mode web detecte.',
            },
          ],
          'connect',
          true,
        );
      }
    } catch (error) {
      setElectronAvailable(false);
      const message = error instanceof Error ? error.message : String(error);
      appendLogs(
        [
          {
            level: 'error',
            message:
              "Impossible de verifier la disponibilite de l'API Electron.",
            details: { error: message },
          },
        ],
        'connect',
        true,
      );
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const report = await ElectronPrintingApi.connect(defaultConfig);
      setConnectionReport(report);
      setIsConnected(report.connected);
      setPrinterStatus(derivePrinterStatus(report));

      const nextCapabilities =
        report.capabilities ?? defaultConfig.capabilities;
      setCapabilities(nextCapabilities ? { ...nextCapabilities } : null);

      const diagnostics: PrinterDiagnosticEntry[] = report.diagnostics?.length
        ? report.diagnostics
        : [
            {
              level: (report.connected
                ? 'info'
                : 'error') as PrinterDiagnosticEntry['level'],
              message: report.connected
                ? 'Connexion IPP reussie.'
                : report.error ?? 'Connexion IPP echouee.',
            },
          ];
      appendLogs(diagnostics, 'connect', true);

      if (!report.connected && report.error) {
        pushLog('error', report.error, 'connect');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setConnectionReport(null);
      setIsConnected(false);
      setPrinterStatus('error');
      appendLogs(
        [
          {
            level: 'error',
            message: 'Connexion IPP echouee.',
            details: { error: message },
          },
        ],
        'connect',
        false,
      );
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await ElectronPrintingApi.disconnect();
      setIsConnected(false);
      setPrinterStatus('unknown');
      setCapabilities(null);
      setConnectionReport(null);
      pushLog('info', 'Deconnexion effectuee.', 'connect');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushLog('error', `Erreur de deconnexion: ${message}`, 'connect');
    }
  };

  const createBlankPdf = async (
    option: ProfileOption,
    pageCount: number,
  ): Promise<string> => {
    const safePageCount =
      Number.isFinite(pageCount) && pageCount > 0 ? Math.floor(pageCount) : 2;
    const totalPages = Math.min(32, Math.max(2, safePageCount)); // cap for safety
    const pagesMarkup = Array.from({ length: totalPages })
      .map(
        (_, index) =>
          `<div class="page"><span class="blank-placeholder" aria-hidden="true">${
            index + 1
          }</span></div>`,
      )
      .join('\n');

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      @page { size: ${option.sourceFormat ?? option.format}; margin: 12mm; }
      html, body { margin: 0; padding: 0; background: #ffffff; }
      .page {
        display: block;
        min-height: 100vh;
        break-after: page;
        page-break-after: always;
        background: #ffffff;
      }
      .page:last-child {
        break-after: auto;
        page-break-after: auto;
      }
      .blank-placeholder {
        display: block;
        width: 100%;
        height: 100%;
        opacity: 0;
      }
    </style>
  </head>
  <body>
    ${pagesMarkup}
  </body>
</html>`;

    const pdf = await ElectronPrintingApi.renderToPDF({
      html,
      fileName: `ricoh-test-${option.id.toLowerCase()}.pdf`,
      pdfOptions: {
        marginsType: 1,
        preferCSSPageSize: true,
        printBackground: false,
      },
    });

    return pdf.path;
  };

  const handleTestProfile = async (option: ProfileOption) => {
    if (!isConnected) {
      pushLog('error', 'Imprimante non connectee.', 'print');
      return;
    }

    setActiveProfile(option.id);
    pushLog('info', `Preparation du test ${option.label}`, 'print');

    try {
      const capabilityRequirements = {
        supportsA3: option.format === 'A3',
        supportsFolding: option.requiresFolding ?? false,
        supportsStapling: option.requiresStapling ?? false,
        supportsSaddleStitch: option.requiresStapling ? true : undefined,
        a3Ready: option.format === 'A3' ? true : undefined,
      };

      const supported = await ElectronPrintingApi.validateCapabilities(
        capabilityRequirements,
      );
      if (!supported) {
        pushLog(
          'warning',
          `Options non supportees pour ${option.label}.`,
          'print',
          capabilityRequirements,
        );
        return;
      }

      const rawFinishings = finishingsOverrides[option.id];
      let finishingsOverride: number[] | undefined;

      if (rawFinishings && rawFinishings.trim().length > 0) {
        const parsed = rawFinishings
          .split(',')
          .map(token => Number(token.trim()))
          .filter(value => Number.isFinite(value));

        if (parsed.length === 0) {
          pushLog(
            'error',
            `Codes finishings invalides pour ${option.label}: "${rawFinishings}"`,
            'print',
          );
          return;
        }

        finishingsOverride = parsed;
      }

      const pageCount = pageCounts[option.id] ?? 2;
      const sourcePdfPath = await createBlankPdf(option, pageCount);

      const job: PrintJob = {
        id: `test-${option.id}-${Date.now()}`,
        type: option.jobType,
        partOrderId: 0,
        content: React.createElement('div', null),
        metadata: {
          pageCount,
          groupKey: 'test',
          format: option.format,
          requiresFolding: option.requiresFolding ?? false,
          requiresStapling: option.requiresStapling ?? false,
          profileId: option.id,
          finishingsOverride,
          originalPartOrder: {} as any,
        },
        electronPath: sourcePdfPath,
      };

      if (option.jobType === 'assembly') {
        const imposed = await ElectronPrintingApi.imposeBooklet(
          sourcePdfPath,
          job,
        );
        job.electronPath = imposed.path;
        job.metadata.pageCount = imposed.pageCount;
        job.metadata.format = imposed.sheetFormat ?? option.format;
        job.metadata.orientation = 'landscape';
        job.metadata.margin = 'none';
      }

      const result = await ElectronPrintingApi.print({
        jobs: [job],
        printerConfig: defaultConfig,
      });

      if (result.success) {
        pushLog(
          'info',
          `Test ${option.label} envoye (${result.method}).`,
          'print',
        );
      } else {
        pushLog('error', `Echec du test ${option.label}.`, 'print');
      }

      if (result.warnings) {
        result.warnings.forEach(warning =>
          pushLog('warning', warning, 'print'),
        );
      }
      if (finishingsOverride && finishingsOverride.length > 0) {
        pushLog(
          'info',
          `Finishings forces: [${finishingsOverride.join(', ')}]`,
          'print',
        );
      }
      pushLog('info', `Pages envoyees: ${pageCount}`, 'print');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushLog(
        'error',
        `Erreur pendant le test ${option.label}: ${message}`,
        'print',
      );
    } finally {
      setActiveProfile(null);
    }
  };

  const getStatusIcon = () => {
    switch (printerStatus) {
      case 'online':
        return <FiCheckCircle className="text-green-500" />;
      case 'error':
        return <FiXCircle className="text-red-500" />;
      default:
        return <FiSettings className="text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (printerStatus) {
      case 'online':
        return 'En ligne';
      case 'error':
        return 'Erreur';
      case 'offline':
        return 'Hors ligne';
      default:
        return 'Inconnu';
    }
  };

  const stateLabel = connectionReport?.state
    ? [
        connectionReport.state.keyword,
        connectionReport.state.code !== undefined
          ? `#${connectionReport.state.code}`
          : undefined,
      ]
        .filter(Boolean)
        .join(' ')
    : undefined;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FiPrinter />
            <span>Imprimante Ricoh IM-C4500</span>
            {getStatusIcon()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span>Statut</span>
              <Badge
                variant={
                  printerStatus === 'online'
                    ? 'default'
                    : printerStatus === 'unknown'
                    ? 'secondary'
                    : 'destructive'
                }
              >
                {getStatusText()}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleConnect}
                disabled={isConnecting || isConnected || !electronAvailable}
                size="sm"
              >
                {isConnecting ? 'Connexion...' : 'Se connecter'}
              </Button>
              <Button
                onClick={handleDisconnect}
                disabled={!isConnected}
                variant="outline"
                size="sm"
              >
                Deconnecter
              </Button>
            </div>
            {connectionReport?.printerName && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FiInfo />
                <span>Nom annonce: {connectionReport.printerName}</span>
              </div>
            )}
            {stateLabel && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FiInfo />
                <span>Etat IPP: {stateLabel}</span>
              </div>
            )}
            {connectionReport?.state?.reasons &&
              connectionReport.state.reasons.length > 0 && (
                <div className="ml-6 text-xs text-muted-foreground space-y-1">
                  {connectionReport.state.reasons.map((reason, index) => (
                    <div key={`${reason}-${index}`}>- {reason}</div>
                  ))}
                </div>
              )}
            {connectionReport?.mediaReady?.length ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <FiInfo />
                <span>
                  Media prets: {connectionReport.mediaReady.join(', ')}
                </span>
              </div>
            ) : null}
            {!electronAvailable && (
              <p className="text-xs text-red-500">
                API Electron indisponible - tests IPP desactives.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Page de test IPP</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Envoie un job de 2 pages blanches pour valider les profils
              utilises sans gaspiller de contenu sensible.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              {PROFILE_OPTIONS.map(option => (
                <div
                  key={option.id}
                  className="border rounded-md p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {option.description}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleTestProfile(option)}
                      disabled={
                        !isConnected ||
                        activeProfile === option.id ||
                        !electronAvailable
                      }
                    >
                      {activeProfile === option.id ? 'Envoi...' : 'Lancer'}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline">Format {option.format}</Badge>
                    <Badge variant="outline">
                      {option.requiresStapling
                        ? 'Agrafage requis'
                        : 'Sans agrafage'}
                    </Badge>
                    <Badge variant="outline">Type {option.jobType}</Badge>
                  </div>
                  <div className="space-y-1">
                    <label
                      className="text-xs font-medium text-muted-foreground"
                      htmlFor={`pages-${option.id}`}
                    >
                      Nombre de pages (pair recommandé)
                    </label>
                    <Input
                      id={`pages-${option.id}`}
                      type="number"
                      min={2}
                      step={2}
                      value={pageCounts[option.id]}
                      onChange={event =>
                        setPageCounts(prev => ({
                          ...prev,
                          [option.id]: Number(event.target.value) || 2,
                        }))
                      }
                      className="h-8 text-xs"
                    />
                  </div>
                  {option.supportsCustomFinishings && (
                    <div className="space-y-1">
                      <label
                        className="text-xs font-medium text-muted-foreground"
                        htmlFor={`finishings-${option.id}`}
                      >
                        Codes finishings (laisser vide pour défaut)
                      </label>
                      <Input
                        id={`finishings-${option.id}`}
                        value={finishingsOverrides[option.id]}
                        onChange={event =>
                          setFinishingsOverrides(prev => ({
                            ...prev,
                            [option.id]: event.target.value,
                          }))
                        }
                        placeholder="ex: 8 ou 8,20"
                        className="h-8 text-xs"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!isConnected && (
              <p className="text-xs text-muted-foreground">
                Connectez l'imprimante avant de lancer un test.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {capabilities && (
        <Card>
          <CardHeader>
            <CardTitle>Capacites detectees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {[
                { label: 'Format A3', value: capabilities.supportsA3 },
                { label: 'Pliage', value: capabilities.supportsFolding },
                { label: 'Agrafage', value: capabilities.supportsStapling },
                {
                  label: 'Decalage',
                  value: capabilities.supportsOffsetStacking,
                },
                {
                  label: 'Saddle stitch',
                  value: capabilities.supportsSaddleStitch,
                },
                { label: 'A3 charge', value: capabilities.a3Ready },
              ]
                .filter(item => item.value !== undefined)
                .map(item => (
                  <div key={item.label} className="flex items-center space-x-2">
                    {item.value ? (
                      <FiCheckCircle className="text-green-500" />
                    ) : (
                      <FiXCircle className="text-red-500" />
                    )}
                    <span>{item.label}</span>
                  </div>
                ))}
            </div>
            {capabilities.finisher && (
              <p className="mt-2 text-xs text-muted-foreground">
                Finisher detecte: {capabilities.finisher}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {logs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Journal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {logs.map(entry => (
                <div key={entry.id} className="flex items-start gap-2 text-xs">
                  <Badge
                    variant={
                      entry.level === 'error'
                        ? 'destructive'
                        : entry.level === 'warning'
                        ? 'outline'
                        : 'secondary'
                    }
                  >
                    {entry.level.toUpperCase()}
                  </Badge>
                  <div className="space-y-1">
                    <div className="font-mono">{entry.message}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleTimeString()} ·{' '}
                      {entry.source}
                    </div>
                    {entry.details && (
                      <pre className="text-[10px] text-muted-foreground whitespace-pre-wrap">
                        {JSON.stringify(entry.details, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RicohTestPage;
